import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, Loader2, X } from "lucide-react";
import { TRANSLATION_LANGUAGES } from "@rec/shared";
import { api } from "@/lib/api";
import { toast } from "@/components/Toaster";
import { TranslateConfirmDialog } from "@/components/TranslateConfirmDialog";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useServerText } from "@/i18n";
import { useFormat } from "@/i18n/format";

/**
 * Export a recording as a file.
 *
 * Two very different costs live behind one dialog, and the dialog's job is to
 * keep them distinct:
 *
 *  - **Download original** is free and instant — a presigned link, no job.
 *  - **Export with subtitles** re-encodes the whole video to burn the cues in
 *    (soft tracks get stripped by chat apps, which is where exports go), and a
 *    translated export first needs the translation, which spends the
 *    workspace's model credit. Each step is its own explicit click.
 *
 * Exports run in the background; the list below the form is live and the
 * download button appears per export when its encode finishes.
 */
export function ExportDialog({
  recordingId,
  hasTranscript,
  onClose,
}: {
  recordingId: string;
  hasTranscript: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["ai", "common"]);
  const st = useServerText();
  const f = useFormat();
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState<string>("original");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Which language is awaiting the consent dialog before translating. */
  const [confirmTranslate, setConfirmTranslate] = useState<string | null>(null);
  /** The picker's pending choice among the 93 supported languages. */
  const [addLanguage, setAddLanguage] = useState("");

  const translations = useQuery({
    queryKey: ["translations", recordingId],
    queryFn: () => api.listTranslations(recordingId),
    // Live while any translation is running, quiet otherwise.
    refetchInterval: (query) =>
      (query.state.data?.translations ?? []).some((t) => t.status === "generating")
        ? 4000
        : false,
  });
  const sourceLanguage = translations.data?.sourceLanguage ?? null;

  const exports = useQuery({
    queryKey: ["exports", recordingId],
    queryFn: () => api.listExports(recordingId),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((e) => e.status === "generating") ? 5000 : false,
  });

  const translate = useMutation({
    mutationFn: (lang: string) => api.requestTranslation(recordingId, lang),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["translations", recordingId] });
    },
  });

  const cancelTranslation = useMutation({
    mutationFn: (lang: string) => api.cancelTranslation(recordingId, lang),
    onSuccess: () => {
      toast(t("export.translationCancelled"), "success");
      void queryClient.invalidateQueries({ queryKey: ["translations", recordingId] });
    },
  });

  const startExport = useMutation({
    mutationFn: () => api.requestExport(recordingId, language),
    onSuccess: () => {
      toast(t("export.exportStarted"), "success");
      void queryClient.invalidateQueries({ queryKey: ["exports", recordingId] });
    },
  });

  const downloadOriginal = useMutation({
    mutationFn: () => api.getDownloadUrl(recordingId),
    onSuccess: (url) => window.location.assign(url),
  });

  const downloadExport = useMutation({
    mutationFn: (exportId: string) => api.getExportDownloadUrl(recordingId, exportId),
    onSuccess: (url) => window.location.assign(url),
  });

  const translationFor = (code: string) =>
    (translations.data?.translations ?? []).find((t) => t.language === code);

  const selectedReady =
    language === "original"
      ? hasTranscript
      : translationFor(language)?.status === "ready";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("export.title")}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl border border-border bg-surface p-5 shadow-[var(--shadow-md)]">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-[16px] font-semibold text-text">{t("export.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-surface-hover hover:text-text"
            aria-label={t("common:actions.close")}
          >
            <X size={16} />
          </button>
        </div>

        {/* The free path first. */}
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-border p-3">
          <div>
            <p className="text-[13.5px] font-medium text-text">{t("export.downloadOriginal")}</p>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-text-muted">
              {t("export.downloadOriginalDetail")}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={downloadOriginal.isPending}
            onClick={() => downloadOriginal.mutate()}
          >
            {downloadOriginal.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Download size={13} />
            )}
            {t("common:actions.download")}
          </Button>
        </div>

        {/* The burned-subtitle path. */}
        <fieldset className="mt-4">
          <legend className="text-[12px] font-medium text-text-muted">
            {t("export.burnedLegend")}
          </legend>
          {!hasTranscript ? (
            <p className="mt-2 text-[12.5px] leading-relaxed text-text-muted">
              {t("export.noTranscript")}
            </p>
          ) : translations.isError ? (
            // isError before rendering choices — a failed list would show every
            // language as "Not translated yet" with a button that spends money.
            <p className="mt-2 text-[12.5px] leading-relaxed text-danger">
              {t("export.translationsFailed")}{" "}
              <button
                type="button"
                className="underline"
                onClick={() => void translations.refetch()}
              >
                {t("common:actions.tryAgain")}
              </button>
            </p>
          ) : (
            <div className="mt-2 space-y-1.5">
              <SubtitleChoice
                selected={language === "original"}
                onSelect={() => setLanguage("original")}
                label={t("export.originalLanguage")}
                detail={t("export.originalDetail")}
              />
              {/* One row per EXISTING translation — 93 supported languages
                  cannot be 93 rows; new ones arrive through the picker below. */}
              {(translations.data?.translations ?? []).map((row) => {
                const meta = TRANSLATION_LANGUAGES.find((l) => l.code === row.language);
                const label = meta
                  ? t("export.languageLabel", {
                      name: f.languageName(meta.code),
                      native: meta.nativeLabel,
                    })
                  : row.language;
                return (
                  <SubtitleChoice
                    key={row.language}
                    selected={language === row.language}
                    onSelect={() => {
                      if (row.status === "ready") setLanguage(row.language);
                    }}
                    label={label}
                    detail={
                      row.status === "ready"
                        ? t("export.translationReady")
                        : row.status === "generating"
                          ? t("export.translating")
                          : row.error
                            ? st(row.error)
                            : t("export.translationFailed")
                    }
                    trailing={
                      row.status === "ready" ? (
                        <Check size={14} className="text-success" />
                      ) : row.status === "generating" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={cancelTranslation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelTranslation.mutate(row.language);
                          }}
                          title={t("export.cancelTitle")}
                        >
                          <Loader2 size={12} className="animate-spin" />
                          {t("common:actions.cancel")}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={translate.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmTranslate(row.language);
                          }}
                          title={t("export.retryTitle")}
                        >
                          {t("common:actions.retry")}
                        </Button>
                      )
                    }
                  />
                );
              })}

              {/* Add any of the 93 supported languages. */}
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-border p-2.5">
                <select
                  value={addLanguage}
                  onChange={(e) => setAddLanguage(e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-border bg-bg-subtle px-2 py-1.5 text-[13px] text-text focus:border-accent focus:outline-none"
                >
                  <option value="">{t("export.addLanguage")}</option>
                  {TRANSLATION_LANGUAGES.filter(
                    (l) =>
                      l.code !== sourceLanguage &&
                      !translationFor(l.code),
                  ).map((l) => (
                    <option key={l.code} value={l.code}>
                      {t("export.languageLabel", {
                        name: f.languageName(l.code),
                        native: l.nativeLabel,
                      })}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!addLanguage || translate.isPending}
                  onClick={() => setConfirmTranslate(addLanguage)}
                >
                  {t("export.translate")}
                </Button>
              </div>
            </div>
          )}
        </fieldset>

        <div className="mt-4 flex items-center justify-end gap-2">
          {hasTranscript && language !== "original" && !selectedReady ? (
            <span className="mr-auto text-[12px] text-text-faint">
              {t("export.translateFirst")}
            </span>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            disabled={!selectedReady || startExport.isPending}
            onClick={() => startExport.mutate()}
            title={t("export.exportTitle")}
          >
            {startExport.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
            {t("export.exportVideo")}
          </Button>
        </div>

        {/* Past and running exports. */}
        {exports.isError ? (
          <p className="mt-4 border-t border-border pt-3 text-[12.5px] text-danger">
            {t("export.exportsFailed")}{" "}
            <button type="button" className="underline" onClick={() => void exports.refetch()}>
              {t("common:actions.tryAgain")}
            </button>
          </p>
        ) : (exports.data ?? []).length > 0 ? (
          <div className="mt-4 border-t border-border pt-3">
            <p className="text-[12px] font-medium text-text-muted">{t("export.exports")}</p>
            <div className="mt-1.5 space-y-1">
              {(exports.data ?? []).map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-1.5"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] text-text">
                      {job.subtitleLanguage === "original"
                        ? t("export.originalLanguage")
                        : f.languageName(job.subtitleLanguage)}
                      {" · "}
                      <span className="text-text-faint">
                        {f.dateTime(job.createdAt, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                    </p>
                    {job.status === "failed" ? (
                      <p className="text-[11.5px] text-danger">{st(job.error)}</p>
                    ) : null}
                  </div>
                  {job.status === "ready" ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={downloadExport.isPending}
                      onClick={() => downloadExport.mutate(job.id)}
                    >
                      <Download size={13} />
                      {t("common:actions.download")}
                    </Button>
                  ) : job.status === "generating" ? (
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-text-faint">
                      <Loader2 size={12} className="animate-spin" />
                      {t("export.encoding")}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {confirmTranslate ? (
        <TranslateConfirmDialog
          sourceLanguage={sourceLanguage}
          targetLanguage={confirmTranslate}
          onConfirm={() => {
            translate.mutate(confirmTranslate);
            setConfirmTranslate(null);
            setAddLanguage("");
          }}
          onClose={() => setConfirmTranslate(null)}
        />
      ) : null}
    </div>
  );
}

function SubtitleChoice({
  selected,
  onSelect,
  label,
  detail,
  trailing,
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
  detail: string;
  trailing?: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 transition-colors",
        selected ? "border-accent bg-accent-subtle/30" : "border-border hover:border-border-strong",
      )}
    >
      <input type="radio" name="subtitle-language" checked={selected} onChange={onSelect} />
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium text-text">{label}</span>
        <span className="block text-[12px] leading-relaxed text-text-muted">{detail}</span>
      </span>
      {trailing}
    </label>
  );
}
